import { useState } from 'react';
import { Users, Search, Loader, Target, Award } from 'lucide-react';
import { matchUserToFormations, matchUserToCertifications } from '../../services/recommendations';

export default function MatchingPanel() {
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [targetType, setTargetType] = useState('formation');
  const [filters, setFilters] = useState({
    level: '',
    sector: ''
  });
  const [error, setError] = useState(null);

  const handleMatch = async () => {
    setLoading(true);
    setError(null);
    try {
      let result;
      const filterObj = {};
      if (filters.level) filterObj.level = filters.level;
      if (filters.sector) filterObj.sector = filters.sector;

      if (targetType === 'formation') {
        result = await matchUserToFormations(null, filterObj); // null = utilisateur connecté
      } else {
        result = await matchUserToCertifications(null, filterObj);
      }

      setMatches(result.matches || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du matching');
      console.error('Erreur matching:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score > 0.7) return 'bg-green-100 text-green-700 border-green-200';
    if (score > 0.5) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (score > 0.3) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Users className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Matching Intelligent</h2>
          <p className="text-sm text-gray-600">Correspondances optimales avec votre profil</p>
        </div>
      </div>

      {/* Options */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type de cible</label>
          <select
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="formation">Formations</option>
            <option value="certification">Certifications RNCP</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
          <select
            value={filters.level}
            onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tous</option>
            <option value="niveau_3">Niveau 3</option>
            <option value="niveau_4">Niveau 4</option>
            <option value="niveau_5">Niveau 5</option>
            <option value="niveau_6">Niveau 6</option>
            <option value="niveau_7">Niveau 7</option>
            <option value="niveau_8">Niveau 8</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Secteur</label>
          <input
            type="text"
            value={filters.sector}
            onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
            placeholder="Ex: Informatique"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Bouton */}
      <button
        onClick={handleMatch}
        disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Matching en cours...
          </>
        ) : (
          <>
            <Target className="w-5 h-5" />
            Lancer le matching
          </>
        )}
      </button>

      {/* Erreur */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Résultats */}
      {matches.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            {matches.length} correspondance{matches.length > 1 ? 's' : ''} trouvée{matches.length > 1 ? 's' : ''}
          </h3>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {matches.map((match, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{match.title}</h4>
                  <div className="flex flex-wrap gap-2">
                    {match.level && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                        {match.level}
                      </span>
                    )}
                    {match.sector && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                        {match.sector}
                      </span>
                    )}
                  </div>
                </div>

                <div className={`px-4 py-2 rounded-lg border font-bold ${getScoreColor(match.score)}`}>
                  {Math.round(match.score * 100)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && matches.length === 0 && !error && (
        <div className="mt-6 text-center py-12 text-gray-500">
          <Award className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Lancez le matching pour trouver les meilleures correspondances</p>
        </div>
      )}
    </div>
  );
}
